import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Ban, Loader2, Plus, Trash2, ShieldAlert, Phone, Mail } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inp = "w-full px-3 py-2 rounded-md bg-[#0A0A0A] border border-white/15 text-white text-sm outline-none focus:border-[#F2B705]";

export default function DnrManager({ token, onAuthError }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [list, setList] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [f, setF] = useState({ name: "", email: "", phone: "", reason: "" });
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/dnr`, auth).then((r) => setList(r.data))
      .catch((e) => { if (e.response?.status === 401) onAuthError?.(); });
    axios.get(`${API}/dnr/attempts`, auth).then((r) => setAttempts(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!f.email.trim() && !f.phone.trim()) { toast.error("Enter an email or phone number."); return; }
    if (!f.reason.trim()) { toast.error("Enter a reason — it's permanently logged."); return; }
    setAdding(true);
    try {
      const { data } = await axios.post(`${API}/dnr`, f, auth);
      toast.success(`Added to Do-Not-Rent.${data.auto_declined > 0 ? ` ${data.auto_declined} pending reservation(s) auto-declined.` : ""}`);
      setF({ name: "", email: "", phone: "", reason: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add to the list."); }
    finally { setAdding(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Remove ${d.name || d.email || d.phone} from the Do-Not-Rent list?`)) return;
    try {
      await axios.delete(`${API}/dnr/${d.id}`, auth);
      toast.success("Removed from the list."); load();
    } catch { toast.error("Could not remove."); }
  };

  if (!list) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#F2B705]" /></div>;

  return (
    <div className="space-y-5" data-testid="dnr-manager">
      <div>
        <h2 className="font-display font-black text-2xl flex items-center gap-2"><Ban className="text-red-400" size={22} /> Do Not Rent List</h2>
        <p className="text-white/50 text-sm mt-1">{list.length} person(s) blocked. Anyone on this list is automatically declined when they submit a reservation request — matched by email or phone.</p>
      </div>

      {/* Add person */}
      <div className="rounded-lg border border-red-500/30 bg-red-500/[0.04] p-4" data-testid="dnr-add-form">
        <div className="text-xs font-bold text-red-300 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Plus size={13} /> Add Person to Do-Not-Rent</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <input data-testid="dnr-add-name" className={inp} placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <input data-testid="dnr-add-email" className={inp} placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          <input data-testid="dnr-add-phone" className={inp} placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
          <input data-testid="dnr-add-reason" className={inp} placeholder="Reason (required)" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
        </div>
        <button data-testid="dnr-add-submit" onClick={add} disabled={adding}
          className="mt-3 flex items-center gap-1.5 bg-red-500 text-white text-sm font-bold px-4 py-2 rounded-md hover:bg-red-400 transition-colors disabled:opacity-60">
          {adding ? <Loader2 className="animate-spin" size={14} /> : <Ban size={14} />} Block This Person
        </button>
        <p className="text-white/35 text-[11px] mt-2">Email or phone is required — they don't need to have rented before. Any pending reservation requests that match are auto-declined instantly.</p>
      </div>

      {/* Blocked people */}
      <div className="space-y-2" data-testid="dnr-list">
        {list.length === 0 && <p className="text-white/40 text-sm border border-white/10 rounded-lg bg-[#0A0A0A] p-5 text-center">Nobody is on the Do-Not-Rent list.</p>}
        {list.map((d) => (
          <div key={d.id} data-testid={`dnr-row-${d.id}`} className="bg-[#0C0C0C] border border-red-500/25 rounded-lg px-4 py-3 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-white">{d.name || "Unknown name"}</span>
                <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/40 flex items-center gap-1"><Ban size={10} /> Do Not Rent</span>
              </div>
              <div className="text-white/50 text-xs mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5">
                {d.email && <span className="flex items-center gap-1"><Mail size={11} /> {d.email}</span>}
                {d.phone && <span className="flex items-center gap-1"><Phone size={11} /> {d.phone}</span>}
                {d.created_at && <span>Added {String(d.created_at).slice(0, 10)}</span>}
                {d.added_by && <span>by {d.added_by}</span>}
              </div>
              {d.reason && <div className="text-red-300 text-xs mt-1">Reason: {d.reason}</div>}
            </div>
            <button data-testid={`dnr-remove-${d.id}`} onClick={() => remove(d)}
              className="flex items-center gap-1.5 border border-white/15 text-white/60 text-xs font-bold px-3 py-2 rounded-md hover:bg-white hover:text-black transition-colors shrink-0">
              <Trash2 size={13} /> Remove
            </button>
          </div>
        ))}
      </div>

      {/* Blocked attempts */}
      <div data-testid="dnr-attempts">
        <h3 className="font-display font-bold text-lg flex items-center gap-2 mb-2"><ShieldAlert size={17} className="text-amber-300" /> Blocked Attempts</h3>
        {attempts.length === 0 ? <p className="text-white/35 text-sm">No blocked booking/reservation attempts yet.</p> : (
          <div className="bg-[#0C0C0C] border border-white/10 rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-white/35 border-b border-white/10">
                <th className="px-3 py-2">When</th><th className="px-3 py-2">Who Tried</th><th className="px-3 py-2">Matched Entry</th>
              </tr></thead>
              <tbody className="divide-y divide-white/5">
                {attempts.map((a) => (
                  <tr key={a.id}>
                    <td className="px-3 py-2 text-white/50 text-xs whitespace-nowrap">{fmtDateTime(a.at || "")}</td>
                    <td className="px-3 py-2 text-white/80">{a.name} <span className="text-white/40 text-xs">{a.email} {a.phone}</span></td>
                    <td className="px-3 py-2 text-red-400 text-xs">{a.matched_dnr_email}{a.reason ? ` (${a.reason})` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
